"use client";

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { merchantQueryKeys } from "./merchant-query-keys";
import { serverApi } from "@/openapi/server";
import {
  getAllMerchants,
  getMerchantById,
  setMerchantShop,
} from "@/app/admin/merchants/_api/merchant-reads";
import type {
  GetAllMerchantsParameter,
  GetAllMerchantsResponse,
  MerchantByIdResponse,
} from "@/app/admin/merchants/_api/merchant-reads";

/**
 * Hook to fetch all merchants (admin only)
 */
export function useAllMerchants(
  params: GetAllMerchantsParameter,
  options?: Omit<UseQueryOptions<GetAllMerchantsResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: merchantQueryKeys.list({
      page: params?.page,
      size: params?.size,
      term: params?.term,
      sort: params?.sort,
      networkId: params?.networkId,
      regions: params?.regions,
      merchantStatuses: params?.merchantStatuses?.map(String),
      contractStatuses: params?.contractStatuses?.map(String),
      hasShop: params?.hasShop,
    }),
    queryFn: () => getAllMerchants(params, serverApi),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

/**
 * Hook to fetch merchants for filter comboboxes (admin only)
 */
export function useMerchantsForFilter(
  params: { term?: string; page?: number; size?: number },
  options?: Omit<UseQueryOptions<GetAllMerchantsResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: merchantQueryKeys.list(params),
    queryFn: () => getAllMerchants(params, serverApi),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook to fetch a single merchant by ID (admin only)
 */
export function useMerchantById(
  id: string,
  options?: Omit<UseQueryOptions<MerchantByIdResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: merchantQueryKeys.detail(id),
    queryFn: () =>
      getMerchantById(id, serverApi),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!id,
    ...options,
  });
}

export function useSetMerchantShop(merchantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shopId: string | null) => setMerchantShop(merchantId, shopId),
    onSuccess: (updated) => {
      queryClient.setQueryData(merchantQueryKeys.detail(merchantId), updated);
    },
  });
}